import { convertDateToYYYYMMDDFormat, STATUSPAGE } from '@repo/ui';
import { useFormik } from 'formik';
import { notifyError } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { downloadReport } from 'presentation/app/modules/Reports/common/helpers/download-report';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';

type AccessProfileType = 'STUDENT' | 'PROFESSIONAL';

const PROFILE_OPTIONS = [
  { label: 'Estudante', value: 'STUDENT' },
  { label: 'Profissional', value: 'PROFESSIONAL' },
] as { label: string; value: AccessProfileType }[];

export const useAccessReport = () => {
  const { organizationService, courseService, reportService } = useService();

  const [organizations, setOrganizations] = useState<unknown[]>([]);
  const [courses, setCourses] = useState<unknown[]>([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingExportExcel, setLoadingExportExcel] = useState(STATUSPAGE.DONE);

  const today = useMemo(() => new Date(), []);
  const firstDayOfMonth = useMemo(
    () => new Date(today.getFullYear(), today.getMonth(), 1),
    [today],
  );

  const validation = useFormik({
    initialValues: {
      entity: null,
      course: null,
      profile: PROFILE_OPTIONS[0],
      starts: convertDateToYYYYMMDDFormat(firstDayOfMonth),
      ends: convertDateToYYYYMMDDFormat(today),
    },
    validationSchema: Yup.object({
      entity: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      course: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      profile: Yup.object()
        .shape({
          label: Yup.string().required(),
          value: Yup.string().required(),
        })
        .required('Campo é obrigatório!'),
      starts: Yup.date().required('Campo é obrigatório!'),
      ends: Yup.date().required('Campo é obrigatório!'),
    }),
    onSubmit: async (values) => {
      setLoadingExportExcel(STATUSPAGE.LOADING);

      await reportService
        .exportAccessReportExcel({
          starts: values.starts,
          ends: values.ends,
          profileType: values.profile.value,
          organizationHashId: values.entity?.value,
          courseHashId: values.course?.value,
        })
        .then((response) => {
          downloadReport(response, {
            filename: 'relatorio-de-acessos.xlsx',
            type: 'excel',
          });
        })
        .catch((error) => {
          setLoadingExportExcel(STATUSPAGE.ERROR);
          notifyError(
            error.message || 'Erro ao exportar relatório de acessos.',
          );
        })
        .finally(() => {
          setLoadingExportExcel(STATUSPAGE.DONE);
        });
    },
  });

  const handleExportExcelClick = useCallback(() => {
    validation.handleSubmit();
  }, [validation]);

  useEffect(() => {
    const loadOrganizations = async () => {
      setLoadingOrganizations(true);

      await organizationService
        .getAllOrganizationsUser()
        .then((response) => setOrganizations(response))
        .catch((error) => console.log(error))
        .finally(() => setLoadingOrganizations(false));
    };

    loadOrganizations();
  }, [organizationService]);

  useEffect(() => {
    const loadCourses = async () => {
      setLoadingCourses(true);

      await courseService
        .getAllCoursesUser({
          entityId: validation.values.entity?.value || undefined,
        })
        .then((response) => setCourses(response))
        .catch((error) => console.log(error))
        .finally(() => setLoadingCourses(false));
    };

    loadCourses();
  }, [courseService, validation.values.entity?.value]);

  return {
    courses,
    handleExportExcelClick,
    loadingCourses,
    loadingExportExcel,
    loadingOrganizations,
    organizations,
    profileOptions: PROFILE_OPTIONS,
    validation,
    firstDayOfMonth,
    today,
  };
};
